import React, { useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { AuthContext } from "../authcontext";
import UnauthorizedLayout from "../../components/layouts/unauthorizedlayout";
import { Result } from "antd";
import { setAccessToken  } from "../../api/tokenmanager";
import {syncOktaUserAndGetGuid, getUser} from "../../services/userservice";

export const OktaAuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { authState, oktaAuth } = useOktaAuth();

    const [userGuid, setUserGuid] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [displayName, setDisplayName] = useState<string | null>(null);
    const [userSyncFailed, setUserSyncFailed] = useState(false);
    const [isUserActive, setIsUserActive] = useState<boolean | null>(null);
    const [isAuthReady, setIsAuthReady] = useState(false);  
    const [roles, setRoles] = useState<string[]>([]);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const [accessToken, setAccessTokenState] = useState<string | null>(null);
    const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(null);
    const isOnCallbackRoute =
        window.location.pathname === "/login/callback";

    const isAuthenticated = !!userGuid;

    const login = async () => {
        await oktaAuth.signInWithRedirect();
    };

    const logout = async () => {
        await oktaAuth.signOut();
    };

    useEffect(() => {
        if (!authState || authState.isPending) {
            return;
        }

        if (authState.isAuthenticated && authState.idToken?.claims) {
            const claims = authState.idToken.claims;  
            const sub = claims.sub as string;
            const emailClaim = claims.email as string | undefined;
            const fullName = claims.name as string | undefined;

            let firstName = "";
            let lastName = "";

            if (fullName) {
                const parts = fullName.trim().split(" ");
                firstName = parts[0];
                lastName = parts.slice(1).join(" ");
            }

            const token = authState.accessToken?.accessToken;
            setEmail(emailClaim ?? null);
            setDisplayName(fullName ?? null);
            setAccessTokenState(token ?? null);
            setAccessToken(token ?? null);  
      
            const syncUser = async () => {
              try {
                const guid = await syncOktaUserAndGetGuid({
                  sub,
                  firstName,
                  lastName,
                  email: emailClaim,
                });
            
                setUserGuid(guid);
                
                // Fetch user roles and active status
                try {
                  const userResponse = await getUser(guid);
                  
                  setIsUserActive(userResponse.data.active);

                  const userRoles = userResponse.data.roles?.map(r => r.roleName) || [];
                  setRoles(userRoles);

                  const hasAdmin = userRoles.includes('Admin');
                  const hasRead = userRoles.includes('Read');

                  if (!hasAdmin && !hasRead) {
                      // Security Failsafe: Treat users with zero valid roles as deactivated
                      // This neutralizes legacy roles like 'Developer' or 'QA'
                      setIsUserActive(false);
                  }
                  
                  // Admin gets full access, everyone else gets Read-Only
                  setIsReadOnly(!hasAdmin);
                } catch (roleError) {
                  console.error("Failed to fetch user roles", roleError);
                  // Default to read-only on error
                  setRoles([]);
                  setIsReadOnly(true);
                }

              } catch {
                setUserSyncFailed(true);
              } finally {
                setIsAuthReady(true);
              }
            };
            syncUser();

            return;
        }

        setIsAuthReady(true);
    }, [authState]);

    useEffect(() => {
        if (
            !isOnCallbackRoute &&
            isAuthReady &&
            !authState?.isPending &&
            !isAuthenticated  &&
            !userSyncFailed             
        ) {
            login();
        }
    }, [
        isOnCallbackRoute,
        isAuthReady,
        authState?.isPending,
        isAuthenticated,
    ]);

    const [expiresAt, setExpiresAt] = useState<number | null>(null);

    useEffect(() => {
        if (authState?.accessToken?.expiresAt) {
            setExpiresAt(authState.accessToken.expiresAt);
        }
    }, [authState?.accessToken]);

    useEffect(() => {
        if (!expiresAt) {
            setSessionTimeRemaining(null);
            return;
        }       

        const interval = setInterval(() => {
            
            const currentTime = Math.floor(Date.now() / 1000);
            const timeRemaining = expiresAt - currentTime;

            if (timeRemaining <= 0) {
                logout();
            } else if (timeRemaining <= 300) { // 5 minutes
                setSessionTimeRemaining(timeRemaining);
            } else {
                setSessionTimeRemaining(null);
            }
        }, 1000); // Check every second

        return () => clearInterval(interval);
    }, [expiresAt]);

    const maintainSession = async (): Promise<boolean> => {
        try {
            await oktaAuth.tokenManager.renew('accessToken');
            await oktaAuth.tokenManager.renew('idToken');
            setSessionTimeRemaining(null);
            return true;
        } catch (error) {
            console.error("Failed to renew token", error);
            // We return false here instead of redirecting so the UI can warn the user
            // to save their work manually before the timer hits 0.
            return false;
        }
    };

    const providerValue = {
        userId: userGuid,
        email,
        displayName,
        roles,
        isReadOnly,
        accessToken,               
        isAuthenticated,
        isAuthReady,
        userSyncFailed,
        sessionTimeRemaining,
        login,
        logout,
        maintainSession,
    };

    if (userSyncFailed) {
        return (
            <AuthContext.Provider value={providerValue}>
                <UnauthorizedLayout>
                    <Result
                        status="500"
                        title="Something Went Wrong"
                        subTitle="We couldn't verify your account. Please try again later or contact support."
                    />
                </UnauthorizedLayout>
            </AuthContext.Provider>
        );
    }

    if (isUserActive === false) {
        return (
            <AuthContext.Provider value={providerValue}>
                <UnauthorizedLayout>
                    <Result
                        status="403"
                        title="Account Deactivated"
                        subTitle="Your account has been deactivated. Please contact an administrator to restore access."
                    />
                </UnauthorizedLayout>
            </AuthContext.Provider>
        );
    }

    if (!isAuthReady && !isOnCallbackRoute) {
        // Prevent rendering the application until the user profile and roles are completely fetched
        return null;
    }

    if (!isAuthenticated && !isOnCallbackRoute) {
        return null;
    }

    return (
        <AuthContext.Provider value={providerValue}>
            {children}
        </AuthContext.Provider>
    );
};